import React, { useState, useEffect, useRef } from 'react';

/* External */
import { useTranslation } from 'react-i18next';
import produce from 'immer';

/* Material UI */
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';

/* Project */
import { splitToLotId } from 'utils/dataReader';
import { truthty } from 'utils/functions';
import QRScanner from '../../../../commons/components/QRScanner';
import Entries from '../Entries';

const parseLotIdFromCode = (raw) => {
  if (!raw) return '';
  const code = String(raw).trim();

  if (/^\d+$/.test(code)) return code;

  if (code.startsWith('PK,')) {
    const pakPart = code
      .split(',')
      .find((part) => part.startsWith('p:'))
      ?.split(':')[1];
    return pakPart && /^\d+$/.test(pakPart) ? pakPart : '';
  }

  const legacyMatch = code.match(/id\s+(\d+)/i);
  return legacyMatch ? legacyMatch[1] : '';
};

function EntryForm(props) {
  const { t } = useTranslation();
  const { controls } = props;

  const [state, setState] = useState({
    loading: false,
    errorMsg: '',
  });
  const isSmallScreen = window.innerWidth < 600;
  const [count, setCount] = useState(0);
  const [inputVal, setInputVal] = useState('');
  const [manualMode, setManualMode] = useState(true);
  const savedCallback = useRef();
  const {
    isOpen,
    classes,
    process,
    objects,
    toggleDelete,
    formControls,
    toggleForm,
    qrReader,
    qrCode,
    getProductsRows,
    getProductsHeaders,
    entryLots,
    printAllFnc,
    onChangeFnc,
    submit,
  } = props;

  function validateCode(code) {
    const msg = '';
    const id = parseLotIdFromCode(code);
    onChangeFnc('entry', { id }, {});
    submit(id);
    setState(
      produce((draft) => {
        // eslint-disable-next-line no-param-reassign
        draft.loading = false;
      }),
    );
    qrReader({}, '');
    return msg;
  }

  function callback() {
    // eslint-disable-next-line no-undef
    const input = document.getElementById('lotInput');
    if (truthty(input)) {
      input.focus();
    }
    if (count <= 0 && truthty(qrCode)) {
      setCount(count + 1);
    }
    if (count > 0 && inputVal !== qrCode) {
      setInputVal(qrCode);
      setCount(0);
    } else if (count > 0 && inputVal === qrCode) {
      const errorMsg = validateCode(qrCode);
      if (errorMsg === '') {
        submit();
      } else {
        setState(
          produce((draft) => {
            // eslint-disable-next-line no-param-reassign
            draft.errorMsg = errorMsg;
          }),
        );
      }
    }
  }

  useEffect(() => {
    savedCallback.current = callback;
  });

  useEffect(() => {
    function tick() {
      // @ts-ignore
      savedCallback.current();
    }

    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, []);

  function onKeyPress(event) {
    if (!state.loading) {
      setState(
        produce((draft) => {
          // eslint-disable-next-line no-param-reassign
          draft.loading = true;
        }),
      );
    }
    if (event.keyCode === 221) {
      qrReader(event, '');
      setCount(0);
    } else if (event.keyCode === 51) {
      const lotId = Number(parseLotIdFromCode(qrCode));
      if (lotId > 0) {
        onChangeFnc('entry', { lotId }, {});
      }
    }
  }

  const addLot = () => {
    const parsedLotId = Number(controls.lotId);
    if (parsedLotId > 0) {
      onChangeFnc('entry', { lotId: parsedLotId }, {});
      submit(parsedLotId);
    }
  };

  const handleLotNumber = (value) => {
    onChangeFnc('entry', { lotId: value === '' ? '' : Number(value) }, {});
  };

  const handleQrReader = async (value) => {
    const lotId = Number(parseLotIdFromCode(value));
    onChangeFnc('entry', { lotId }, {});
    submit(lotId);
  };

  // -----------------------------------
  const title = !manualMode && t('process.scanQr');

  const [camaraIsOpen, setCamaraIsOpen] = useState(false);

  const handleCamaraButton = () => {
    setCamaraIsOpen(!camaraIsOpen);
  };

  return (
    <Dialog
      // @ts-ignore
      TransitionProps={{ 'data-module': 'entry' }}
      fullWidth
      onClose={toggleForm}
      aria-labelledby="simple-dialog-title"
      open={isOpen}>
      <DialogTitle>{t('process.addEntryTitle')}</DialogTitle>
      <DialogContent>
        <Box display="flex" justifyContent="center">
          <FormControlLabel
            control={<Switch checked={manualMode} onChange={() => setManualMode(!manualMode)} />}
            label={t('process.manualMode')}
          />
        </Box>
        {state.loading ? (
          <Grid container justifyContent="center" spacing={3}>
            <Grid item xs={12}>
              <Typography align="center" style={{ marginTop: 35 }}>
                {t('process.loading')}
              </Typography>
            </Grid>
            <Grid item>
              <CircularProgress />
            </Grid>
          </Grid>
        ) : (
          <Typography align="center" style={{ marginTop: 35 }}>
            {truthty(state.errorMsg) ? state.errorMsg : title}
          </Typography>
        )}
        {manualMode ? (
          <Grid container style={{ marginTop: 30, marginBottom: 25 }}>
            <Grid item xs={6}>
              <Box display="flex" justifyContent="flex-end">
                <TextField
                  label={isSmallScreen ? t('process.pakNumber') : t('process.enterPakNumber')}
                  type="number"
                  value={controls.lotId}
                  onChange={(e) => handleLotNumber(e.target.value)}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                style={{ height: '100%' }}
                display="flex"
                justifyContent="center"
                alignItems="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={addLot}
                  disabled={Number(controls.lotId) <= 0}>
                  {t('process.enter')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={1} style={{ width: '100%' }} justifyContent="center">
            <Grid item sm={1} xs={1}>
              <TextField
                style={{
                  width: 0,
                  overflow: 'hidden',
                }}
                value={qrCode}
                onChange={qrReader}
                fullWidth
                id="lotInput"
                label={t('process.readLot')}
                onKeyDown={onKeyPress}
                autoComplete="off"
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <div style={{ textAlign: 'center' }}>
                <Button variant="contained" color="primary" onClick={handleCamaraButton}>
                  {camaraIsOpen ? t('process.closeCamera') : t('process.openCamera')}
                </Button>
              </div>
            </Grid>
            <Grid item sm={8} xs={12}>
              <div style={{ textAlign: 'center' }}>
                {camaraIsOpen && <QRScanner onScan={handleQrReader} />}
                <div id="reader" />
              </div>
            </Grid>
          </Grid>
        )}
        <Grid container spacing={1} style={{ width: '100%' }} justifyContent="center">
          <Entries
            isGroupedInfo={formControls.headers.isGroupedInfo}
            classes={classes}
            process={process}
            lots={objects.lots}
            objects={objects}
            toggleDelete={toggleDelete}
            getProductsRows={getProductsRows}
            getProductsHeaders={getProductsHeaders}
            entryLots={entryLots}
            printAllFnc={printAllFnc}
            isSmallScreen={isSmallScreen}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={toggleForm} data-module="entry" color="primary" variant="outlined">
          {t('Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EntryForm;
